import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, CheckCircle2, Zap, Shield, TrendingUp, Terminal } from "lucide-react";

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  badge: string;
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
  sections: {
    title: string;
    items: string[];
  }[];
}

const changelog: ChangelogEntry[] = [
  {
    version: "2.3.0",
    date: "2025-10-11",
    title: "MCP Server Integration & npm Package Published",
    icon: Sparkles,
    badge: "Latest",
    badgeVariant: "default",
    sections: [
      {
        title: "Model Context Protocol (MCP) Server",
        items: [
          "Natural language usage in Claude Code - no slash commands needed!",
          "Install once: claude mcp add localdocs -- npx --package=@projectassistant/localdocs -y localdocs-mcp",
          "Just say: 'Search our LocalDocs for React documentation'",
          "Or: 'Export our FastAPI docs to this project'",
          "Automatic tool detection and execution",
        ],
      },
      {
        title: "5 MCP Tools Available",
        items: [
          "localdocs_search - Search team documentation library",
          "localdocs_export - Export documentation to project directories",
          "localdocs_add - Add new documentation URLs",
          "localdocs_login - Get authentication instructions",
          "localdocs_status - Check authentication status",
        ],
      },
      {
        title: "npm Package Published",
        items: [
          "Package renamed from @localdocs/cli to @projectassistant/localdocs",
          "Published to npm registry under @projectassistant organization",
          "Install globally: npm install -g @projectassistant/localdocs",
          "Or use with npx: npx @projectassistant/localdocs login",
          "Package size optimized to 30.3 kB (5 essential files)",
        ],
      },
      {
        title: "Shared Authentication",
        items: [
          "Both CLI and MCP use the same 30-day access tokens",
          "Authenticate once with: npx @projectassistant/localdocs login",
          "Config stored in ~/.localdocs-cli/config.json",
          "Works seamlessly across all tools",
        ],
      },
      {
        title: "Documentation Updates",
        items: [
          "All Help pages updated with new package name",
          "Added comprehensive MCP usage examples",
          "Created CHANGELOG.md for npm package versioning",
          "Updated CLI Integration tab with natural language examples",
          "Added email domain validation note to Security tab",
        ],
      },
    ],
  },
  {
    version: "2.2.0",
    date: "2025-10-10",
    title: "LocalDocs CLI for Claude Code Integration",
    icon: Terminal,
    badge: "Feature",
    badgeVariant: "secondary",
    sections: [
      {
        title: "OAuth Device Flow Authentication",
        items: [
          "Seamless authentication like Claude Code (no manual token extraction)",
          "Visit /cli-auth page and enter 6-character code",
          "30-day access tokens with automatic polling",
          "Device codes expire in 10 minutes for security",
        ],
      },
      {
        title: "Security Enhancements",
        items: [
          "User status checking on every request (industry standard)",
          "Immediate token revocation when users are disabled/removed",
          "SHA-256 token hashing in database",
          "Zero-trust architecture: always verify user status",
        ],
      },
      {
        title: "Command-Line Interface",
        items: [
          "New CLI tool (cli/localdocs-cli.js) for terminal access",
          "Commands: login, search, export, add, status",
          "Beautiful terminal UX with spinner and progress",
          "Config stored in ~/.localdocs-cli/config.json",
        ],
      },
      {
        title: "Claude Code Integration",
        items: [
          "Slash command: /localdocs-search for searching team documentation",
          "Direct CLI invocation from Claude Code sessions",
          "Export documentation to docs/localdocs/ for project context",
          "Tag-based filtering (react, fastapi, docker, etc.)",
        ],
      },
      {
        title: "Export Edge Function",
        items: [
          "New supabase/functions/export-documents endpoint",
          "JWT authentication with user-scoped queries",
          "Three export formats: claude (AI-optimized), markdown, json",
          "Filter by tags or document IDs",
        ],
      },
    ],
  },
  {
    version: "2.1.0",
    date: "2025-10-10",
    title: "AI Agent Enhanced with Auto-Download",
    icon: Sparkles,
    badge: "Feature",
    badgeVariant: "secondary",
    sections: [
      {
        title: "No More Duplicate Suggestions",
        items: [
          "Edge Function now sends your existing URLs (up to 100) to Claude",
          'Explicit instruction: "DO NOT suggest URLs that already exist"',
          "Lowercase URL comparison for case-insensitive matching",
        ],
      },
      {
        title: '"Add All Documents" Button',
        items: [
          "Appears when AI suggests 2+ docs",
          "Click once to download all suggestions",
          "Progress tracking with summary toast",
          "Shows: Added X documents. Skipped Y duplicates. Failed: Z",
        ],
      },
      {
        title: "Bulk Discovery Mode",
        items: [
          "Updated prompts encourage comprehensive discovery",
          'Example: "Download all MUI documentation"',
          "AI will suggest 10-20 official docs (getting started, API, components, examples, deployment)",
          "Welcome message explains the capability",
        ],
      },
      {
        title: "Better UX",
        items: [
          'New greeting explains auto-filtering: "I\'ll automatically filter out documentation you already have"',
          'Updated placeholder: "Download all MUI documentation"',
          "Enhanced hint examples for bulk operations",
        ],
      },
    ],
  },
  {
    version: "2.0.1",
    date: "2025-10-10",
    title: "Upgraded to Claude Sonnet 4.5",
    icon: TrendingUp,
    badge: "Performance",
    badgeVariant: "secondary",
    sections: [
      {
        title: "AI Model Upgrade",
        items: [
          "Updated from claude-3-5-sonnet-20241022 to claude-sonnet-4-5-20250929",
          "Zero cost increase (same $3/$15 per MTok pricing)",
          '"Highest intelligence across most tasks"',
          "Better reasoning for document discovery suggestions",
          "More reliable JSON response generation",
          "Current knowledge cutoff (July 2025 vs April 2024)",
          "Enhanced complex agent capabilities",
          "Extended thinking support",
        ],
      },
    ],
  },
  {
    version: "2.0.0",
    date: "2025-10-10",
    title: "Comprehensive Security Hardening",
    icon: Shield,
    badge: "Security",
    badgeVariant: "destructive",
    sections: [
      {
        title: "Authentication & Authorization",
        items: [
          "Eliminated 9 vulnerabilities (3 CRITICAL + 6 HIGH severity)",
          "Implemented JWT authentication for 10 Edge Functions",
          "Zero trust architecture (user_id derived from JWT only)",
          "Standardized error responses (401/403)",
          "Ownership verification for document operations",
        ],
      },
      {
        title: "Cryptographic Improvements",
        items: [
          "SHA-256 hashing (64-character collision-resistant)",
          "Replaced weak 32-bit hash implementation",
          "URL normalization (lowercase protocol/host, strip trailing slashes)",
          "Database migration: hash column VARCHAR(8) → VARCHAR(64)",
        ],
      },
      {
        title: "Security Hygiene",
        items: [
          "Zero hard-coded credentials",
          "Environment variable validation with fail-fast errors",
          "PII sanitization (gated logs with import.meta.env.DEV)",
          "100% credential hygiene achieved",
          "Security score: 17% → 100%",
        ],
      },
      {
        title: "Documentation",
        items: [
          "6 comprehensive security guides created (89KB total)",
          "Deployment checklist with pre/post validation",
          "Authentication implementation guide",
          "Hash migration guide with backfill strategies",
          "Quick reference for common patterns",
          "Release notes with upgrade instructions",
        ],
      },
    ],
  },
];

export default function ChangelogPage() {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Changelog</h1>
        <p className="text-muted-foreground mt-2">
          Stay up to date with the latest features, improvements, and security updates
        </p>
      </div>

      <div className="space-y-6">
        {changelog.map((entry, index) => {
          const Icon = entry.icon;
          return (
            <Card key={index} className="relative">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">{entry.title}</CardTitle>
                        <Badge variant={entry.badgeVariant}>{entry.badge}</Badge>
                      </div>
                      <CardDescription className="mt-1">
                        Version {entry.version} • {entry.date}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {entry.sections.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      {section.title}
                    </h3>
                    <ul className="space-y-2 ml-7">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="text-sm text-muted-foreground">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            How to Use
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Before (manual clicking):</h4>
            <code className="text-xs bg-card p-3 rounded-md block">
              You: "I need MUI docs"
              <br />
              AI: [Suggests 15 docs, some duplicates]
              <br />
              You: [Clicks "Add to Library" 15 times, skips duplicates manually]
            </code>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Now (auto-download):</h4>
            <code className="text-xs bg-card p-3 rounded-md block">
              You: "Download all MUI documentation"
              <br />
              AI: [Suggests 15 NEW official MUI docs, filters existing]
              <br />
              You: [Clicks "Add All 15 Documents"]
              <br />
              Result: ✅ Added 15 documents. Skipped 0 duplicates.
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
